import { SparkleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/**
 * The workbench's one AI-action treatment: every control that asks Mora's
 * model to write or judge something. It is the paid builder's "Draft this"
 * grammar (`StepAssistAction`: flat button, filled `SparkleIcon` in `--skill`)
 * plus the AI-temporary contract's accept wash (`bg-skill/10 text-skill-text
 * hover:bg-skill/15`, `references/ai-temporary.md`). `--skill` is the AI
 * attribution colour; `--primary` and the neutral CTA stay for committed
 * actions such as Approve.
 */
export const AI_ACTION_CLASS =
  'gap-1.5 bg-skill/10 text-skill-text hover:bg-skill/15 hover:text-skill-text';

export interface AiActionButtonProps extends Omit<ButtonProps, 'variant' | 'children'> {
  busy?: boolean;
  children: React.ReactNode;
}

export const AiActionButton = React.forwardRef<HTMLButtonElement, AiActionButtonProps>(
  ({ busy, className, children, size = 'compact', disabled, ...props }, ref) => (
    <Button
      ref={ref}
      type="button"
      variant="flat"
      size={size}
      data-ai-action=""
      disabled={disabled || busy}
      className={cn(AI_ACTION_CLASS, className)}
      {...props}
    >
      {busy ? (
        <Spinner className="size-3.5" />
      ) : (
        <SparkleIcon aria-hidden weight="fill" className="size-3.5" />
      )}
      {children}
    </Button>
  ),
);
AiActionButton.displayName = 'AiActionButton';
